import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { CarImagePlaceholder } from "@/components/cars/CarImagePlaceholder";

export interface CarCardImage {
  url: string | null;
  cdnThumbnail?: string | null;
  cdnMedium?: string | null;
  cdnFull?: string | null;
  width?: number | null;
  height?: number | null;
}

interface CarCardImageCarouselProps {
  images: CarCardImage[];
  title: string;
  priority?: boolean;
  noImageLabel: string;
  className?: string;
  // `sizes` for the responsive srcset. The carousel is mobile-only (hidden at
  // sm:+), so this describes the card's rendered width on phones. Defaults to
  // 100vw (full-width card, e.g. search results); homepage rails render cards
  // 2-up (w-[46%]) and pass ~50vw so the browser picks the 800w variant
  // instead of the 2400w full — the dominant page-weight lever (Task #6465).
  sizes?: string;
  measuredImages?: boolean;
}

// Mobile-only swipeable image carousel for CarCard. Uses native CSS
// scroll-snap so a horizontal swipe pages through the listing photos
// without ever intercepting the card's tap-to-open navigation (the card
// is an <a>, and a scroll gesture does not fire a click). Renders a
// single static image gracefully when only one photo is available, and
// the shared "no image" placeholder when there are none.
export function CarCardImageCarousel({
  images,
  title,
  priority,
  noImageLabel,
  className,
  sizes = "100vw",
  measuredImages,
}: CarCardImageCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  // Per-slide load-failure flags: a legacy photo whose origin is a dead 404
  // renders the neutral placeholder instead of the browser's broken-image icon.
  const [errored, setErrored] = useState<Record<number, boolean>>({});

  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el || el.clientWidth === 0) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActive((cur) => (cur === idx ? cur : idx));
  }, []);

  if (images.length === 0) {
    return (
      <div
        className={cn(
          "flex h-full w-full items-center justify-center bg-muted text-muted-foreground",
          className,
        )}
      >
        <span className="text-sm">{noImageLabel}</span>
      </div>
    );
  }

  const showDots = images.length > 1;

  return (
    <div className={cn("relative h-full w-full", className)}>
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((img, i) => {
          const srcSet =
            [
              img.cdnThumbnail && `${img.cdnThumbnail} 400w`,
              img.cdnMedium && `${img.cdnMedium} 800w`,
              img.cdnFull && `${img.cdnFull} 2400w`,
            ]
              .filter(Boolean)
              .join(", ") || undefined;
          return (
            <div key={i} className="relative h-full w-full shrink-0 snap-center">
              {errored[i] ? (
                <CarImagePlaceholder label={noImageLabel} />
              ) : (
                <img
                  src={img.cdnMedium || img.cdnThumbnail || img.url || undefined}
                  width={measuredImages && img.url === (img.cdnMedium || img.cdnThumbnail || img.url) ? img.width ?? undefined : undefined}
                  height={measuredImages && img.url === (img.cdnMedium || img.cdnThumbnail || img.url) ? img.height ?? undefined : undefined}
                  srcSet={srcSet}
                  // The carousel is hidden at sm:+ (desktop renders the single
                  // CarCard <img> instead), but Chromium still fetches hidden
                  // lazy images. The `(min-width: 640px) 1px` clause makes that
                  // wasted desktop fetch resolve to the smallest (400w)
                  // candidate instead of 800w (Task #7873 live audit).
                  sizes={`(min-width: 640px) 1px, ${sizes}`}
                  alt={title}
                  loading={priority && i === 0 ? "eager" : "lazy"}
                  {...(priority && i === 0 ? { fetchPriority: "high" as const } : {})}
                  draggable={false}
                  className="h-full w-full object-cover"
                  onError={() => setErrored((prev) => ({ ...prev, [i]: true }))}
                />
              )}
            </div>
          );
        })}
      </div>

      {showDots && (
        <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex items-center justify-center gap-1.5">
          {images.slice(0, 8).map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1.5 rounded-full bg-white/70 shadow-sm transition-[width,background-color] duration-200",
                i === Math.min(active, 7) ? "w-4 bg-white" : "w-1.5",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
